import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config/config';
import { PortableText } from '@portabletext/react';
import WeeklyPost from '../../components/WeeklyPost/WeeklyPost';
import ContactUs from '../../components/ContactUs/ContactUs';
import styles from './SalvationDetailPage.module.css';
import '../../components/PortableTextStyles.css';

interface SalvationDetail {
  reference: string;
  verses: any; // Can be string or portable text
  explanation: string;
  detailedTeaching: {
    title: string;
    date: string;
    content: any; // Can be string or portable text
    type: 'salvation';
    references: string[];
  };
}

const SalvationDetailPage: React.FC = () => {
  const { verseReference } = useParams<{ verseReference: string }>();
  const navigate = useNavigate();
  const [salvationDetail, setSalvationDetail] = useState<SalvationDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [postId, setPostId] = useState<string | null>(null);

  // First, find the post ID for the given verse reference
  useEffect(() => {
    const findPostId = async () => {
      setLoading(true);
      try {
        // Get all salvation posts
        console.log(`Finding post ID for reference: ${verseReference}`);
        const response = await axios.get(`${config.API_BASE_URL}/salvation-posts`);
        
        if (response.data && response.data.length > 0) {
          // Find the post with the matching reference
          const post = response.data.find((p: any) => 
            p.reference === verseReference || p.title === verseReference
          );
          
          if (post) {
            console.log(`Found post with ID: ${post.id || post._id}`);
            setPostId(post.id?.toString() || post._id);
          } else {
            console.error(`No post found with reference: ${verseReference}`);
            setError(`Verse "${verseReference}" not found.`);
            setLoading(false);
          }
        } else {
          setError('No salvation posts found.');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error finding post ID:', error);
        setError('Failed to find the post. Please try again later.');
        setLoading(false);
      }
    };

    if (verseReference) {
      findPostId();
    } else {
      setError('No verse reference provided.');
      setLoading(false);
    }
  }, [verseReference]);

  // Then, fetch the post details using the ID
  useEffect(() => {
    const fetchSalvationDetail = async () => {
      if (!postId) return;
      
      try {
        console.log(`Fetching salvation post with ID: ${postId}`);
        const response = await axios.get(`${config.API_BASE_URL}/salvation-posts/${postId}`);
        
        if (response.data) {
          console.log('API Response:', response.data);
          
          // Transform the post to match the SalvationDetail interface
          const formattedDetail: SalvationDetail = {
            reference: response.data.reference || response.data.title,
            verses: response.data.verses || '',
            explanation: response.data.content && Array.isArray(response.data.content) && response.data.content[0]?.children ? 
              response.data.content[0].children.map((child: any) => child.text).join(' ') : 
              'No explanation available',
            detailedTeaching: {
              title: response.data.title,
              date: response.data.date,
              content: response.data.content,
              type: 'salvation',
              references: response.data.references || []
            }
          };
          
          setSalvationDetail(formattedDetail);
          setError(null);
        }
      } catch (error) {
        console.error('Error fetching salvation detail:', error);
        setError(`Post with ID "${postId}" not found or could not be loaded.`);
        setSalvationDetail(null);
      } finally {
        setLoading(false);
      }
    };

    if (postId) {
      fetchSalvationDetail();
    }
  }, [postId]);

  const handleBackClick = () => {
    navigate('/salvation');
  };

  if (loading) {
    return (
      <div className={styles['salvation-detail-page']}>
        <div className={styles['loading']}>Loading verse details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['salvation-detail-page']}>
        <div className={styles['error']}>
          <h2>Error</h2>
          <p>{error}</p>
          <button className={styles['back-button']} onClick={handleBackClick}>
            Back to Salvation
          </button>
        </div>
      </div>
    );
  }

  if (!salvationDetail) {
    return (
      <div className={styles['salvation-detail-page']}>
        <div className={styles['error']}>
          <h2>Verse Not Found</h2>
          <p>The requested verse could not be found.</p>
          <button className={styles['back-button']} onClick={handleBackClick}>
            Back to Salvation
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['salvation-detail-page']}>
      <div className={styles['salvation-header']}>
        <button className={styles['back-button']} onClick={handleBackClick}>
          ← Back to Salvation
        </button>
        <h1>{salvationDetail.reference}</h1>
      </div>

      <div className={styles['salvation-verses']}>
        <h2>Scripture</h2>
        <div className={styles['verse-text']}>
          {typeof salvationDetail.verses === 'string' ? (
            <p>{salvationDetail.verses}</p>
          ) : (
            <PortableText value={salvationDetail.verses} />
          )}
        </div>
      </div>

      <div className={styles['salvation-teaching']}>
        <WeeklyPost
          title={salvationDetail.detailedTeaching.title}
          date={salvationDetail.detailedTeaching.date}
          content={salvationDetail.detailedTeaching.content}
          type={salvationDetail.detailedTeaching.type}
          references={salvationDetail.detailedTeaching.references || []}
        />
      </div>

      <ContactUs />
    </div>
  );
};

export default SalvationDetailPage;
